import type {
  Booking,
  BookingResponse,
  BookingType,
  CreateBookingDto,
  CreateBookingResponse,
  UpdateBookingTimeDto,
  UpdateBookingTimeResponse,
} from '@/types/booking';
import { useAuth } from '@clerk/nextjs';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/store';
import { getApiBaseUrl } from '@/lib/apiUrl';

export const useBookingService = () => {
  const { getToken } = useAuth();
  const locale = useSelector((state: RootState) => state.language.language);

  const createBooking = async (bookingData: CreateBookingDto): Promise<CreateBookingResponse> => {
    try {
      const token = await getToken();
      const response = await axios.post<CreateBookingResponse>(
        `${getApiBaseUrl()}/bookings`,
        bookingData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        'data' in error.response
      ) {
        const response = error.response as { status: number; data: { message?: string } };
        return {
          statusCode: response.status,
          message: response.data.message || 'Unknown error occurred',
        };
      } else {
        throw new Error('Booking failed due to a network issue.');
      }
    }
  };

  const getBookingDetails = async (id: string): Promise<BookingResponse> => {
    const token = await getToken();
    const response = await axios.get<BookingResponse>(`${getApiBaseUrl()}/bookings/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const findBooking = async (userId: string, courseId: string): Promise<BookingResponse | null> => {
    const token = await getToken();
    const response = await axios.get<BookingResponse>(`${getApiBaseUrl()}/bookings/find-booking`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        userId,
        courseId,
      },
    });
    return response.data ? response.data : null;
  };

  const getBookings = async (): Promise<Booking[]> => {
    const token = await getToken();
    const response = await axios.get<Booking[]>(`${getApiBaseUrl()}/bookings`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const updateBooking = async (
    id: string,
    bookingData: Partial<CreateBookingDto>,
  ): Promise<Booking> => {
    const token = await getToken();
    const response = await axios.put<Booking>(`${getApiBaseUrl()}/bookings/${id}`, bookingData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const updateBookingTime = async (
    id: string,
    updateBookingTimeDto: UpdateBookingTimeDto,
  ): Promise<UpdateBookingTimeResponse> => {
    const token = await getToken();
    const response: AxiosResponse<UpdateBookingTimeResponse> = await axios.put<
      Booking,
      AxiosResponse<UpdateBookingTimeResponse>
    >(`${getApiBaseUrl()}/bookings/${id}/update-time`, updateBookingTimeDto, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const approveBooking = async (id: string): Promise<Booking> => {
    const token = await getToken();
    const response = await axios.put<Booking>(
      `${getApiBaseUrl()}/bookings/${id}/approve`,
      { locale },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  };

  const cancelBooking = async (id: string): Promise<{ success: boolean }> => {
    const token = await getToken();
    const res = await axios.put<void>(
      `${getApiBaseUrl()}/bookings/${id}/cancel`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return { success: res.status === 200 };
  };

  const hasActiveBooking = async (userId: string, courseId: string): Promise<string | null> => {
    const token = await getToken();
    const response = await axios.get<{ activeBooking: string | null }>(
      `${getApiBaseUrl()}/bookings/has-active-booking`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { userId, courseId },
      },
    );
    return response.data.activeBooking;
  };

  const getParticipantsCount = async (courseId: string): Promise<number> => {
    const token = await getToken();
    const response = await axios.get<number>(`${getApiBaseUrl()}/bookings/participants-count`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { courseId },
    });
    return response.data;
  };

  const getUserBookings = async (
    userId: string,
    getPastBookings: boolean,
    limit?: number,
  ): Promise<BookingType[]> => {
    const token = await getToken();
    const pastQuery = getPastBookings ? 'true' : 'false';
    const limitQuery = limit ? `&limit=${limit}` : '';
    const response = await axios.get<BookingType[]>(
      `${getApiBaseUrl()}/bookings/user/${userId}?past=${pastQuery}${limitQuery}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  };

  const getExpertBookings = async (
    expertId: string,
    limit?: number,
    past: boolean = false,
  ): Promise<BookingType[]> => {
    const token = await getToken();
    const response = await axios.get<BookingType[]>(
      `${getApiBaseUrl()}/bookings/expert/${expertId}?limit=${limit}&past=${past}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  };

  const getExpertBookingsDetailsById = async (
    expertExternalId: string,
    bookingId: string,
  ): Promise<BookingResponse> => {
    const token = await getToken();
    const response = await axios.get<BookingResponse>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings-details/${bookingId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  };

  return {
    createBooking,
    getBookingDetails,
    findBooking,
    getBookings,
    updateBooking,
    updateBookingTime,
    approveBooking,
    cancelBooking,
    hasActiveBooking,
    getParticipantsCount,
    getUserBookings,
    getExpertBookings,
    getExpertBookingsDetailsById,
  };
};
